/**
 * @description A context that provides a reference to the actions container of the page header, so it can easily
 *  be shared to any component in the tab content. Its used to inject action buttons (like the "Delete All" button)
 *  into the page header.
 */

import React from 'react';
import { PropTypes } from 'prop-types';

export const ActionsContext = React.createContext(null);

/**
 * @prop children {React.Element | React.Element[]} - react elements to wrap the provider in
 * @prop actionsRef {React.RefObject<*>} - reference to the actions container
 */
export const ActionsProvider = ({ children, actionsRef }) => {
  const [actionsEl, setActionsEl] = React.useState(null);

  /**
   * Sets the actions container element state based on the reference passed in.
   *
   * Its important to use react state when registering the reference so that any components that depend on the
   * actions container element to be defined will be re-rendered once the reference is defined.
   */
  React.useEffect(() => {
    setActionsEl(actionsRef.current);
  }, []);

  return (
    <ActionsContext.Provider value={actionsEl}>
      {children}
    </ActionsContext.Provider>
  );
};

ActionsProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
  actionsRef: PropTypes.shape({ current: PropTypes.object }).isRequired
};
